<template>
  <RouterView
    @redirectToLogin="redirectToLogin"
    @authenticateUser="authenticateUser"
    @refreshToken="refreshToken"
    @logout="logout"
    @updateSnackbarSettings="updateSnackbarSettings"
    @notifyExpiredToken="notifyExpiredToken"
  />

  <VertexProducts />

  <Snackbar
    :snackbarSettings="snackbarSettings"
    @updateSnackbarSettings="updateSnackbarSettings"
  />
</template>

<script setup>
import { onMounted, provide, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { storeToRefs } from "pinia";
import axios from "axios";

import { useAuthenticationStore } from "@/stores/authentication";

import { useSnackbar } from "@/composables/useSnackbar.js";
import { useAuthentication } from "@/composables/useAuthentication.js";

import VertexProducts from "./components/VertexProducts.vue";
import Snackbar from "./components/Snackbar.vue";

const authenticationStore = useAuthenticationStore();
const { authentication } = storeToRefs(authenticationStore);

const { snackbarSettings, updateSnackbarSettings } = useSnackbar();

const {
  authenticateUser: authenticateUserInUseAuthentication,
  refreshToken: refreshTokenInUseAuthentication,
  logout,
  redirectToLogin,
} = useAuthentication();

provide("logout", logout);

const selectedMenuName = ref("dashboard");

provide("selectedMenuName", selectedMenuName);

const route = useRoute();

axios.defaults.headers.common["Authorization"] =
  `Bearer ${authentication.value ?? ""}`;

const getTestRoutes = () => {
  const testRoutes = [
    "testConfigs2",
    "testConnections2",
    "testIpGeolocation2",
    "testLogs2",
    "testPlatforms2",
    "testMariadbTables2",
    "testSrslog2",
  ];

  return testRoutes;
};

const refreshToken = async () => {
  const testRoutes = getTestRoutes();
  if (testRoutes.includes(route.name)) {
    return;
  }

  refreshTokenInUseAuthentication();
};

const authenticateUser = () => {
  const testRoutes = getTestRoutes();
  if (testRoutes.includes(route.name)) {
    return;
  }

  refreshToken();

  authenticateUserInUseAuthentication();
};

const notifyExpiredToken = () => {
  const message = "Your session has expired. Please login again.";
  logout(message);
};

onMounted(() => {
  console.log("🚀 Live Server Node Version:", process.env.VUE_APP_NODE_VERSION);

  const testRoutes = getTestRoutes();
  if (testRoutes.includes(route.name)) {
    return;
  }

  setInterval(
    () => {
      authenticateUserInUseAuthentication();
    },
    1000 * 60 * 5,
  );

  setInterval(async () => {
    if (authentication.value !== null) {
      try {
        const { data } = await axios.get(
          "log-sources-downloads/notifications",
          { timeout: 1000 * 8 },
        );

        if (data.success && data.message.trim() != "") {
          const snackbarSettings = {
            show: true,
            color: "success",
            message: data.message.trim(),
            timer: 6000,
          };
          updateSnackbarSettings(snackbarSettings);
        }
      } catch (error) {
        if (error.code !== "ECONNABORTED") {
          let errorMessage = "An unexpected error occurred.";

          if (error.response) {
            errorMessage = error.response.data.message || error.message;
          } else if (error.request) {
            errorMessage =
              "Network error. Please check your internet connection.";
          } else {
            errorMessage = error.message;
          }

          const snackbarSettings = {
            show: true,
            color: "red accent-2",
            message: errorMessage,
            timer: 3000,
          };
          updateSnackbarSettings(snackbarSettings);

          console.error("API Error:", error);

          notifyExpiredToken();
        }
      }
    }
  }, 1000 * 30);
});

watch(
  () => route.path,
  () => {
    const currentRouteName = route.name;

    const menuTree = {
      dashboard: [
        "configs",
        "logs",
        "platforms",
        "cacheFiles",
        "ipGeolocation",
        "testLogSources",
      ],
      logSources: [
        "logSource",
        "addLogSource",
        "addSyslogLogSource",
        "addAwsLogSource",
        "addSftpLogSource",
        "addAzureadLogSource",
        "addDmarcLogSource",
        "addAuth0LogSource",
        "addGoogleWorkspaceLogSource",
        "addJumpcloudLogSource",
        "logHistory",
        "editSyslogLogSource",
        "editAwsLogSource",
        "editSftpLogSource",
        "editAzureadLogSource",
        "editDmarcLogSource",
        "editAuth0LogSource",
        "editGoogleWorkspaceLogSource",
        "editJumpcloudLogSource",

        "testAwsLogSourceBucket",
        "testAwsLogSourceFiles",
        "testSftpLogSourceFolder",
        "testASftpLogSourceFiles",
        "testCache",
      ],
      downloads: null,
      ipsAndLocations: null,
      maliciousIpsList: null,
      ispServerHostings: null,
      alerts: null,
      dnsLookup: null,
      logsIngested: null,
      reports: [
        "reportsApplicationLogEvents",
        "reportsDmarc",
        "reportsAzure",
        "reportsMicrosoft365",
        "suspiciousOrMaliciousSigninAlerts",
      ],
    };

    const menuNames = Object.keys(menuTree);

    if (menuNames.includes(currentRouteName)) {
      selectedMenuName.value = currentRouteName;
      return;
    }

    for (const menuKey of Object.keys(menuTree)) {
      const menuValue = menuTree[menuKey];
      if (menuValue != null && menuValue.includes(currentRouteName)) {
        selectedMenuName.value = menuKey;
        return;
      }
    }
  },
);
</script>

<style>
html {
  overflow-y: hidden !important;
}

body {
  background: linear-gradient(180deg, #00a0c1 0, #00a0c1 235px, #fff 0);
  background-repeat: no-repeat;
  margin: 0;
  padding: 0;
}

.x-container {
  font-family: "Roboto", sans-serif;
  display: flex;
  max-height: 100%;
  overflow: hidden;
}

.page-content {
  padding: 30px;
  flex-grow: 1;
  height: 100vh;
  overflow-y: scroll;
}
</style>
